import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Table, Button, Modal, Form, Input, DatePicker, Select, message, Space, Tag, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { canManageHolidays, canViewHolidays, canCreateHoliday, isSuperAdmin } from '../utils/permissions';
import dayjs from 'dayjs';
import holidayService from '../services/holidayService';
import organizationService from '../services/organizationService';

function Holidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [year, setYear] = useState(dayjs().year());
  const [category, setCategory] = useState(null);
  const [form] = Form.useForm();
  const { user } = useSelector((state) => state.auth);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(user?.organizationId || undefined);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId),
    [selectedOrganizationId, user]
  );

  // Check permissions
  const canView = canViewHolidays(user);
  const canCreate = canCreateHoliday(user);
  const canManage = canManageHolidays(user);

  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      }
    };

    loadOrganizations();
  }, [user]);

  useEffect(() => {
    if (canView) {
      fetchHolidays();
    }
  }, [year, category, effectiveOrganizationId]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await holidayService.getAll({
        year,
        category: category ? category : undefined,
        organizationId: effectiveOrganizationId,
        page: 1,
        limit: 100
      });
      setHolidays(response.data || []);
    } catch (error) {
      message.error('Failed to fetch holidays');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEditMode(false);
    setSelectedHoliday(null);
    form.resetFields();
    form.setFieldsValue({
      organizationId: effectiveOrganizationId
    });
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setIsEditMode(true);
    setSelectedHoliday(record);
    form.setFieldsValue({
      organizationId: record.organizationId,
      name: record.name,
      date: dayjs(record.date),
      category: record.category,
      description: record.description,
      isOptional: record.isOptional
    });
    setIsModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Holiday',
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await holidayService.delete(record.id);
          message.success('Holiday deleted successfully');
          fetchHolidays();
        } catch (error) {
          message.error('Failed to delete holiday');
          console.error(error);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        date: values.date.toISOString(),
        year: values.date.year(),
        organizationId: isSuperAdmin(user) ? values.organizationId : effectiveOrganizationId
      };

      if (isEditMode) {
        await holidayService.update(selectedHoliday.id, payload);
        message.success('Holiday updated successfully');
      } else {
        await holidayService.create(payload);
        message.success('Holiday created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      fetchHolidays();
    } catch (error) {
      message.error(isEditMode ? 'Failed to update holiday' : 'Failed to create holiday');
      console.error(error);
    }
  };

  const columns = [
    ...(isSuperAdmin(user) ? [{
      title: 'Organization',
      dataIndex: 'organizationId',
      key: 'organizationId',
      render: (orgId) => {
        const org = organizations.find(o => o.id === orgId);
        return org?.name || orgId;
      }
    }] : []),
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
      sorter: (a, b) => new Date(a.date) - new Date(b.date)
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color={category === 'National' ? 'blue' : category === 'Regional' ? 'green' : 'orange'}>
          {category}
        </Tag>
      )
    },
    {
      title: 'Optional',
      dataIndex: 'isOptional',
      key: 'isOptional',
      render: (isOptional) => (isOptional ? 'Yes' : 'No')
    },
    {
      title: 'Created By',
      dataIndex: ['creator', 'firstName'],
      key: 'createdBy',
      render: (text, record) => `${record.creator?.firstName || ''} ${record.creator?.lastName || ''}`.trim()
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    }
  ];

  // Add action column for managers/admin
  if (canManage) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      )
    });
  }

  if (!canView) {
    return (
      <Layout style={{ padding: '20px' }}>
        <Card>
          <p>You don't have permission to view holidays</p>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Holiday Calendar</h1>
        {canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Holiday
          </Button>
        )}
      </div>

      {isSuperAdmin(user) && (
        <div style={{ marginBottom: '16px' }}>
          <Select
            placeholder="Select Organization"
            style={{ width: 300 }}
            value={selectedOrganizationId}
            onChange={setSelectedOrganizationId}
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
        </div>
      )}

      <Space style={{ marginBottom: '20px' }}>
        <Select
          style={{ width: 150 }}
          placeholder="Select Year"
          value={year}
          onChange={setYear}
          options={[
            { label: '2024', value: 2024 },
            { label: '2025', value: 2025 },
            { label: '2026', value: 2026 },
            { label: '2027', value: 2027 }
          ]}
        />

        <Select
          style={{ width: 150 }}
          placeholder="All Categories"
          value={category}
          onChange={setCategory}
          allowClear
          options={[
            { label: 'National', value: 'National' },
            { label: 'Regional', value: 'Regional' },
            { label: 'Optional', value: 'Optional' }
          ]}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={holidays}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 50,
          showSizeChanger: true
        }}
      />

      <Modal
        title={isEditMode ? 'Edit Holiday' : 'Add Holiday'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select an organization' }]}
            >
              <Select
                placeholder="Select Organization"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Holiday Name"
            rules={[{ required: true, message: 'Please enter holiday name' }]}
          >
            <Input placeholder="e.g., Independence Day" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select
              options={[
                { label: 'National', value: 'National' },
                { label: 'Regional', value: 'Regional' },
                { label: 'Optional', value: 'Optional' }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Holiday description" />
          </Form.Item>

          <Form.Item
            name="isOptional"
            label="Optional Holiday?"
            valuePropName="checked"
          >
            <Select
              options={[
                { label: 'No', value: false },
                { label: 'Yes', value: true }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Holidays;
